import { Component, HostListener, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from 'src/app/shared/pipes/translate.pipe';
import {
  buildSubmitPayload,
  clearActiveAttemptId,
  clearMockState,
  formatRemaining,
  loadMockState,
  navigatorSummaryLine,
  progressCounts,
  questionProgress,
  remainingMilliseconds,
  saveMockState,
  statementValue
} from '../mock-state';
import {
  MockAnswerMap,
  MockAttempt,
  MockLocalState,
  MockProgress,
  MockProgressCounts,
  MockQuestion,
  StatementAnswer
} from '../mock.models';
import { MockService } from '../mock.service';
import { isSubscriptionRequired } from '../mock-subscription';

@Component({
  selector: 'app-mock-exam',
  templateUrl: './mock-exam.component.html',
  styleUrls: ['./mock-exam.component.scss']
})
export class MockExamComponent implements OnInit, OnDestroy {
  attempt: MockAttempt | null = null;
  state: MockLocalState = loadMockState('pending');
  isSpinner = true;
  loadError = false;
  submitError = false;
  subscriptionRequired = false;
  timeUp = false;
  running = false;
  submitting = false;
  navOpen = false;
  private wideLayout = false;
  remainingMs = 0;
  remainingLabel = '00:00';
  pendingCounts: MockProgressCounts = { unanswered: 0, partial: 0, answered: 0, flagged: 0 };
  readonly letters = ['A', 'B', 'C', 'D', 'E'];

  @ViewChild('submitConfirm') submitConfirm?: TemplateRef<unknown>;

  private destroyed = new Subject<void>();
  private clock: number | null = null;
  private attemptId = '';
  private submitModal: NzModalRef | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private mockService: MockService,
    private modal: NzModalService,
    private translate: TranslatePipe
  ) {}

  ngOnInit(): void {
    this.wideLayout = this.viewportWidth() >= 768;
    this.navOpen = this.wideLayout;
    this.route.paramMap.pipe(takeUntil(this.destroyed)).subscribe(params => {
      const id = params.get('attemptId');
      if (id) {
        this.load(id);
      }
    });
  }

  ngOnDestroy(): void {
    this.stopClock();
    this.destroyed.next();
    this.destroyed.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    const wide = this.viewportWidth() >= 768;
    if (wide === this.wideLayout) {
      return;
    }
    this.wideLayout = wide;
    this.navOpen = wide;
  }

  viewportWidth(): number {
    return window.innerWidth;
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeUnload(event: BeforeUnloadEvent): void {
    if (this.shouldWarnOnLeave()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  shouldWarnOnLeave(): boolean {
    return this.running && !this.submitting;
  }

  get currentQuestion(): MockQuestion | null {
    if (!this.attempt) {
      return null;
    }
    return this.attempt.questions[this.state.currentIndex] || null;
  }

  letter(index: number): string {
    return this.letters[index - 1] || String(index);
  }

  answerFor(question: MockQuestion, index: number): StatementAnswer {
    return statementValue(this.state.answers[String(question.id)], index);
  }

  choose(question: MockQuestion, index: number, value: boolean): void {
    const questionId = String(question.id);
    if (!this.state.answers[questionId]) {
      this.state.answers[questionId] = {};
    }
    const key = String(index);
    const current = this.state.answers[questionId][key];
    this.state.answers[questionId][key] = current === value ? null : value;
    this.persist();
  }

  toggleFlag(): void {
    const question = this.currentQuestion;
    if (!question) {
      return;
    }
    const id = String(question.id);
    this.state.flags[id] = !this.state.flags[id];
    this.persist();
  }

  isFlagged(question: MockQuestion): boolean {
    return !!this.state.flags[String(question.id)];
  }

  progressOf(question: MockQuestion): MockProgress {
    return questionProgress(question, this.state.answers);
  }

  answeredCount(): number {
    if (!this.attempt) {
      return 0;
    }
    return progressCounts(this.attempt.questions, this.state).answered;
  }

  navigatorSummary(): string {
    if (!this.attempt) {
      return '';
    }
    const counts = progressCounts(this.attempt.questions, this.state);
    return navigatorSummaryLine(
      this.translate.transform('mockNavigatorSummary'),
      counts.answered,
      this.attempt.questions.length,
      counts.flagged
    );
  }

  goTo(index: number): void {
    if (!this.attempt) {
      return;
    }
    if (index < 0 || index >= this.attempt.questions.length) {
      return;
    }
    this.state.currentIndex = index;
    this.persist();
    if (window.innerWidth < 768) {
      this.navOpen = false;
    }
  }

  previous(): void {
    this.goTo(this.state.currentIndex - 1);
  }

  next(): void {
    this.goTo(this.state.currentIndex + 1);
  }

  openSubmit(): void {
    if (!this.attempt || this.submitting || this.subscriptionRequired || !this.submitConfirm) {
      return;
    }
    this.pendingCounts = progressCounts(this.attempt.questions, this.state);
    this.submitModal = this.modal.confirm({
      nzTitle: this.translate.transform('mockSubmitConfirm'),
      nzContent: this.submitConfirm,
      nzOkText: this.translate.transform('mockSubmit'),
      nzCancelText: this.translate.transform('no'),
      nzOnOk: () => this.submit(false)
    });
  }

  submit(auto: boolean): void {
    if (!this.attempt || this.submitting) {
      return;
    }
    this.submitting = true;
    this.submitError = false;
    this.timeUp = auto;
    this.persist();
    const payload: MockAnswerMap = buildSubmitPayload(this.attempt.questions, this.state.answers);
    const id = this.attempt.id;
    this.mockService.submitAttempt(id, payload).pipe(takeUntil(this.destroyed)).subscribe(() => {
      this.running = false;
      this.stopClock();
      clearMockState(String(id));
      clearActiveAttemptId();
      this.router.navigate(['/mock', id, 'result']);
    }, error => {
      this.submitting = false;
      if (isSubscriptionRequired(error)) {
        this.revealSubscriptionPanel();
        return;
      }
      this.submitError = true;
    });
  }

  updateClock(now: number = Date.now()): void {
    if (!this.attempt || !this.running) {
      return;
    }
    this.remainingMs = remainingMilliseconds(this.attempt.expires_at, now);
    this.remainingLabel = formatRemaining(this.remainingMs);
    if (this.remainingMs <= 0 && !this.subscriptionRequired) {
      this.submit(true);
    }
  }

  load(id: string): void {
    this.stopClock();
    this.attemptId = id;
    this.isSpinner = true;
    this.loadError = false;
    this.running = false;
    this.mockService.getAttempt(id).pipe(takeUntil(this.destroyed)).subscribe(attempt => {
      this.attempt = attempt;
      this.isSpinner = false;
      if (attempt.result) {
        this.router.navigate(['/mock', attempt.id, 'result']);
        return;
      }
      this.state = loadMockState(id);
      if (this.state.currentIndex >= attempt.questions.length) {
        this.state.currentIndex = 0;
      }
      this.running = true;
      this.updateClock();
      if (this.remainingMs <= 0) {
        return;
      }
      this.startClock();
    }, error => {
      this.isSpinner = false;
      if (isSubscriptionRequired(error)) {
        this.revealSubscriptionPanel();
        this.loadError = false;
        return;
      }
      this.loadError = true;
    });
  }

  private revealSubscriptionPanel(): void {
    this.subscriptionRequired = true;
    this.submitError = false;
    this.timeUp = false;
    this.submitting = false;
    this.stopClock();
    this.dismissSubmitDialog();
    setTimeout(() => this.scrollSubscriptionIntoView());
  }

  private dismissSubmitDialog(): void {
    const ref: any = this.submitModal;
    this.submitModal = null;
    if (!ref) {
      return;
    }
    setTimeout(() => {
      const overlay = ref.overlayRef;
      if (overlay && typeof overlay.dispose === 'function') {
        overlay.dispose();
        return;
      }
      if (typeof ref.destroy === 'function') {
        ref.destroy();
      }
    });
  }

  private scrollSubscriptionIntoView(): void {
    const panel = document.querySelector('.subscription-panel');
    if (!panel) {
      return;
    }
    const top = panel.getBoundingClientRect().top + window.pageYOffset - 8;
    window.scrollTo(0, Math.max(0, top));
  }

  private persist(): void {
    if (this.attemptId) {
      saveMockState(this.attemptId, this.state);
    }
  }

  private startClock(): void {
    this.stopClock();
    this.clock = window.setInterval(() => this.updateClock(), 1000);
  }

  private stopClock(): void {
    if (this.clock !== null) {
      window.clearInterval(this.clock);
      this.clock = null;
    }
  }
}
